import { useState, useRef, useEffect } from 'react';
import StepOffre, { OFFRE_ADHESION } from '@/features/installer/components/wizard/StepOffre';
import axios from 'axios';
import { Card, CardContent } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Checkbox } from '@/shared/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Zap, ArrowRight, ArrowLeft, Check, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import ClausesConsentement from '@/shared/components/ClausesConsentement';
import RecherchePdl from '@/shared/components/RecherchePdl';
import RetrouverParPdl from '@/shared/components/RetrouverParPdl';
import RechercheAdresse from '@/shared/components/RechercheAdresse';
import useLangue from '@/shared/i18n/useLangue';
import { IDENTITE } from '@/shared/marque';
import BandeauMandatExistant from '@/features/installer/components/BandeauMandatExistant';
import { jetonAgregateurDepuisUrl } from '@/shared/utils/agregateurMandat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Le canal ④ n'a que deux étapes : le mandat est déjà signé, la troisième n'a plus
// d'objet. Afficher un jalon « Signature » qu'on saute ensuite ferait douter le poseur
// d'avoir oublié quelque chose, devant le client.
const ETAPES_CANAL_3 = [
  { id: 1, label: 'Vos infos' },
  { id: 2, label: 'Installation' },
  { id: 3, label: 'Offre' },
  { id: 4, label: 'Signature' },
];
const ETAPES_CANAL_4 = ETAPES_CANAL_3.slice(0, 2);

// Ce que l'étiquette porte dans l'URL. Un seul paramètre, et c'est un jeton : ni
// identifiant d'appareil ni MAC en clair, sinon qui devine un identifiant ouvre un
// dossier (dette du design du 21/08, fermée depuis).
const PARAM_ETIQUETTE = 'a';

// ⚠️ LES TROIS AUTORISATIONS DU MANDAT NE SONT PLUS ÉCRITES ICI, DEPUIS LE 09/09/2026.
//
// Cet écran portait sa propre copie de « modulation », « données » et « audit ». Le CRM
// et le parcours terrain ont cessé de recopier ces phrases le jour où l'on a constaté
// que « deux textes pour un même engagement, c'est un vice de consentement » ; ce
// formulaire est resté dehors parce qu'il est PUBLIC et que la route qui sert le texte
// exigeait un compte. Il en a une maintenant : `/onboarding/texte-consentements`, qui
// appelle la même fonction de service. Remettre ces phrases ici les ferait rediverger au
// prochain changement de rédaction, sans que rien ne le signale.
//
// LES DEUX CI-DESSOUS RESTENT, ET CE N'EST PAS UN OUBLI. Ce canal est le seul à les
// poser, avec ses propres mots : la politique de confidentialité — qui n'est PAS la
// clause de recontact commercial retirée du mandat le 02/09 — et la transmission des
// données techniques à l'agrégateur. Les faire tomber avec les clauses partagées ferait
// perdre deux consentements réellement recueillis. Voir `consentement_rgpd` dans
// `modules/onboarding/routes.py`, à l'endroit où la réponse est enregistrée.
const CONSENT_LABELS = {
  rgpd: "J'ai lu et j'accepte la politique de confidentialité (RGPD).",
  survoltage: "J'autorise EcoFlexi à transmettre mes données techniques à l'agrégateur Survoltage.",
};

export default function OnboardingPage() {
  // Page autonome : elle n a pas de parent qui lui passe la traduction.
  const { t } = useLangue();
  const [step, setStep] = useState(1);
  const [client, setClient] = useState({
    civilite: 'M.', prenom: '', nom: '', adresse: '', complement_adresse: '',
    code_postal: '',
    ville: '', pdl_prm: '', email: '', telephone: '',
  });
  const [asset, setAsset] = useState({
    type: 'PAC', marque: '', modele: '', device_id: '', puissance_kw: '', reference_chantier: '',
  });
  const [deviceCheck, setDeviceCheck] = useState({ status: '', message: '', loading: false });
  // Les trois autorisations du mandat (`CONSENT_KEYS`, texte servi par le serveur) et
  // les deux questions propres à ce canal (`CLAUSES_PROPRES_AU_CANAL`, texte ci-dessus).
  const [consents, setConsents] = useState({
    modulation: false, donnees: false, audit: false, rgpd: false, survoltage: false,
  });
  const [offre, setOffre] = useState(null);
  const [signatureData, setSignatureData] = useState('');
  // L'étiquette scannée, si le parcours vient d'un QR code. `null` = page ouverte à la
  // main : le champ reste libre et le parcours d'avant ne bouge pas.
  const [etiquette, setEtiquette] = useState(null);
  const [etiquetteErreur, setEtiquetteErreur] = useState('');
  const [mandatExistant, setMandatExistant] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  const up = (k, v) => setClient(c => ({ ...c, [k]: v }));
  const upA = (k, v) => setAsset(a => ({ ...a, [k]: v }));

  // La recherche du PDL vit dans `@/shared/components/RecherchePdl`, avec le parcours
  // installateur. Elle etait ecrite ici, appelait le bouchon et remplissait le champ
  // avec un numero fabrique par hachage de l'adresse -- servi tel quel par les deux
  // instances jusqu'au 31/08.

  // L'étiquette collée sur la boîte porte l'identifiant : le poseur ne recopie rien,
  // donc il ne se trompe pas. C'est l'objectif du canal : un dossier constitué en moins
  // d'une minute, sans installer d'application.
  //
  // Le jeton est LU ici, pas consommé : il est brûlé à la création du dossier. Sur un
  // chantier la page est rechargée dès que le réseau tousse, et une étiquette morte au
  // premier affichage laisserait le poseur sans recours devant le client.
  useEffect(() => {
    const jeton = new URLSearchParams(window.location.search).get(PARAM_ETIQUETTE);
    if (!jeton) return;
    axios.get(`${API}/onboarding/activation/${encodeURIComponent(jeton)}`)
      .then(({ data }) => {
        setEtiquette({ jeton, ...data });
        if (data.device_id) upA('device_id', data.device_id);
      })
      .catch(err => setEtiquetteErreur(
        err.response?.data?.detail
        || "Cette étiquette n'a pas pu être lue. Saisissez l'identifiant à la main."));
  }, []);

  // Vérification temps réel Device ID (debounce 500ms)
  useEffect(() => {
    if (!asset.device_id || asset.device_id.length < 4) {
      setDeviceCheck({ status: '', message: '', loading: false });
      return;
    }
    setDeviceCheck(d => ({ ...d, loading: true }));
    const t = setTimeout(async () => {
      try {
        // Même résolution que le parcours installateur, sur les deux canaux : ce
        // formulaire était le seul endroit de l'application où une vérification avait
        // lieu, il est maintenant un appelant parmi d'autres. Le verdict remplit aussi
        // la marque et le modèle, que le client n'a aucune raison de connaître.
        const res = await axios.post(`${API}/onboarding/verify-device`,
                                     { identifiant: asset.device_id });
        setDeviceCheck({ ...res.data, loading: false });
        if (res.data?.materiel?.marque) upA('marque', res.data.materiel.marque);
        if (res.data?.materiel?.modele) upA('modele', res.data.materiel.modele);
        if (res.data?.materiel?.type_suggere) upA('type', res.data.materiel.type_suggere);
      } catch {
        setDeviceCheck({ statut: 'indisponible', vivant: false,
                         message: 'Vérification impossible pour le moment.', loading: false });
      }
    }, 500);
    return () => clearTimeout(t);
  }, [asset.device_id]);

  // Un PDL identifie un logement. S'il est déjà connu, redemander toute la fiche serait
  // une perte de temps sur un chantier, et refaire signer créerait un second mandat
  // concurrent sur le même logement.
  //
  // En cas de doute on retombe sur le parcours LONG, délibérément : un faux négatif
  // coûte deux minutes au poseur, un faux positif sauterait une signature obligatoire.
  const allerEnInstallation = async () => {
    try {
      const { data } = await axios.get(`${API}/onboarding/check-mandate`,
                                       { params: { pdl: client.pdl_prm } });
      setMandatExistant(data.exists && data.mandats_signes > 0 ? data : null);
    } catch {
      setMandatExistant(null);
    }
    setStep(2);
  };

  const STEPS = mandatExistant ? ETAPES_CANAL_4 : ETAPES_CANAL_3;
  // L'adresse email rejoint les champs obligatoires le 03/09, et le serveur refuse la
  // soumission sans elle (`backend/app/core/email_saisie.py`). C'est par ce formulaire
  // que le client s'enregistre lui-meme : c'est aussi la seule occasion de lui demander
  // ou lui envoyer son mandat et son lien d'enregistrement materiel. Sans adresse, les
  // deux envois rendent `skipped / client_sans_email` et personne n'en est averti.
  //
  // `.trim()` et pas une simple verite JavaScript : une adresse faite d'espaces passe le
  // test de verite et se fait refuser par le serveur, qui la lit `.strip()`.
  const step1Valid = client.prenom && client.nom && client.adresse && client.pdl_prm
    && (client.email || '').trim();
  // Le formulaire public suit la même règle que le parcours installateur : tant que la
  // machine n'a pas répondu, l'étape ne se valide pas. Auparavant un équipement
  // simplement « hors ligne » passait, et la fiche partait sur un actif muet.
  const step2Valid = asset.type && asset.marque && asset.modele && asset.device_id
    && deviceCheck.vivant === true;
  const allConsents = Object.values(consents).every(Boolean);
  const step3Valid = allConsents && signatureData;

  // Signature canvas
  const startDraw = (e) => {
    drawing.current = true;
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    const ctx = c.getContext('2d');
    ctx.beginPath();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - r.top;
    ctx.moveTo(x, y);
  };
  const drawMove = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    const ctx = c.getContext('2d');
    // L'encre suit la marque qui sert la page. Ni une classe ni une variable CSS ne
    // peuvent l'atteindre : un contexte canvas attend une couleur en JavaScript, au
    // moment du trace. `IDENTITE` est la source que tout le reste du systeme consomme.
    ctx.strokeStyle = IDENTITE.couleur;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - r.top;
    ctx.lineTo(x, y);
    ctx.stroke();
    setSignatureData(c.toDataURL('image/png'));
  };
  const endDraw = () => { drawing.current = false; };
  const clearSig = () => {
    const c = canvasRef.current;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    setSignatureData('');
  };

  // Le jeton part avec la soumission : c'est LUI que le serveur brûle, et il le fait
  // avant la première écriture. La page n'envoie jamais d'identifiant client, elle
  // n'en a jamais reçu, mais le PDL, que le serveur re-résout.
  const submit = async () => {
    setSubmitting(true); setError('');
    const jeton = etiquette?.jeton;
    try {
      const r = mandatExistant
        ? await axios.post(`${API}/onboarding/associer-device`, {
            pdl: client.pdl_prm, device_id: asset.device_id, jeton,
            type: asset.type, marque: asset.marque, modele: asset.modele,
            puissance_kw: asset.puissance_kw, reference_chantier: asset.reference_chantier,
          })
        : await axios.post(`${API}/onboarding/submit`, {
            client, asset, consents, signature_data: signatureData, jeton,
            // Le choix du Pack et sa signature. Meme regle que le parcours terrain :
            // le serveur s arrete avant le mandat quand c est un refus.
            offre: offre || OFFRE_ADHESION,
            // L'agregateur du mandat (C8a) : le jeton SIGNE du lien de l'operateur, tel quel.
            // Le serveur le verifie ; sans lien, mandat generique.
            agregateur: jetonAgregateurDepuisUrl(window.location.search),
          });
      setResult(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la soumission');
    }
    setSubmitting(false);
  };

  if (result) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4 notranslate" translate="no">
        <Card className="max-w-md w-full"><CardContent className="pt-8 pb-6 text-center">
          <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-neutral-900 mb-2">
            {result.deja_associe ? 'Équipement déjà rattaché' : 'Installation validée'}
          </h1>
          <p className="text-[13px] text-neutral-600 mb-4">
            {result.deja_associe
              ? "Cet équipement était déjà enregistré sur ce dossier : rien n'a été créé en double."
              : <>Votre installation est enregistrée. Statut équipement : <span className="font-medium">{result.thermostat_status === 'en_ligne' ? 'Connecté' : 'En attente de connexion'}</span>.</>}
          </p>
          <p className="text-[11px] text-neutral-400">Référence : {(result.client_id || result.asset_id)?.slice(0, 8).toUpperCase()}</p>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 notranslate" translate="no">
      <header className="bg-marque text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-violet-300" /><span className="font-semibold text-[14px]">Activation thermostat</span></div>
        <span className="text-[11px] text-white/60">EcoFlexi</span>
      </header>
      <div className="px-4 py-4 bg-white border-b border-neutral-100">
        <div className="flex items-center justify-center gap-1.5 max-w-sm mx-auto">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center gap-1.5">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold ${step >= s.id ? 'bg-marque text-white' : 'bg-neutral-200 text-neutral-500'}`}>
                {step > s.id ? <Check className="w-3.5 h-3.5" /> : s.id}
              </div>
              <span className={`text-[11px] font-medium hidden sm:inline ${step >= s.id ? 'text-neutral-700' : 'text-neutral-400'}`}>{s.label}</span>
              {i < STEPS.length - 1 && <div className={`w-6 h-0.5 ${step > s.id ? 'bg-marque' : 'bg-neutral-200'}`} />}
            </div>
          ))}
        </div>
      </div>
      <div className="max-w-lg mx-auto p-4 pb-12">
        {error && <div className="mb-3 p-3 rounded-md bg-red-50 text-red-700 text-[12px] flex items-start gap-2"><AlertCircle className="w-4 h-4 mt-0.5" />{error}</div>}
        {/* Une étiquette illisible ne doit pas fermer le parcours : le poseur saisit
            l'identifiant à la main et la vérification live le refuse s'il se trompe. */}
        {etiquetteErreur && <div className="mb-3 p-3 rounded-md bg-amber-50 text-amber-800 text-[12px] flex items-start gap-2" data-testid="onb-etiquette-erreur"><AlertCircle className="w-4 h-4 mt-0.5" />{etiquetteErreur}</div>}

        {/* STEP 1 */}
        {step === 1 && (
          <div className="space-y-4" data-testid="onb-step1">
            <h2 className="text-lg font-semibold text-neutral-900">Vos informations</h2>
            <div className="rounded-lg border border-neutral-200 bg-white p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[11px] text-neutral-500">{t('field_prenom', "Prénom")} *</Label><Input className="h-9" value={client.prenom} onChange={e => up('prenom', e.target.value)} data-testid="onb-prenom" /></div>
                <div><Label className="text-[11px] text-neutral-500">Nom *</Label><Input className="h-9" value={client.nom} onChange={e => up('nom', e.target.value)} data-testid="onb-nom" /></div>
              </div>
              <div>
                <Label className="text-[11px] text-neutral-500">Adresse *</Label>
                {/* ⚠️ `authentifie={false}` : ce parcours s'ouvre AVANT tout compte —
                    le client scanne un QR, il n'a pas de session. La route
                    authentifiee y repondrait 403, avale en liste vide, et la
                    proposition ne s'afficherait simplement jamais. C'est la seule des
                    quatre portes dans ce cas. */}
                <RechercheAdresse
                  valeur={client.adresse}
                  onChange={v => up('adresse', v)}
                  onChoisir={p => {
                    up('adresse', p.adresse);
                    up('code_postal', p.code_postal);
                    up('ville', p.ville);
                    /* Pas de region ici : ce formulaire n'en porte pas. La resoudre
                       poserait un champ que le POST n'attend pas. */
                  }}
                  classeChamp="h-9"
                  authentifie={false}
                  testid="onb-adresse" />
              </div>
              <div>
                <Label className="text-[11px] text-neutral-500">Complement d&apos;adresse</Label>
                <Input className="h-9" value={client.complement_adresse || ''}
                  onChange={e => up('complement_adresse', e.target.value)}
                  placeholder="Batiment, escalier, etage" data-testid="onb-complement-adresse" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[11px] text-neutral-500">{t('field_code_postal', "Code postal")}</Label><Input className="h-9" value={client.code_postal} onChange={e => up('code_postal', e.target.value)} data-testid="onb-cp" /></div>
                <div><Label className="text-[11px] text-neutral-500">{t('field_ville', "Ville")}</Label><Input className="h-9" value={client.ville} onChange={e => up('ville', e.target.value)} data-testid="onb-ville" /></div>
              </div>
              <div>
                <Label className="text-[11px] text-neutral-500">PDL/PRM (14 chiffres) *</Label>
                <Input className="h-9 font-mono" value={client.pdl_prm} onChange={e => up('pdl_prm', e.target.value.replace(/\D/g, '').slice(0, 14))} data-testid="onb-pdl" />
                {/* Le meme composant que le parcours installateur : meme appel, memes
                    mots, meme refus de proposer un numero qu'on n'a pas trouve. */}
                <div className="mt-1.5">
                  <RecherchePdl client={client} valeurActuelle={client.pdl_prm}
                    onTrouve={pdl => up('pdl_prm', pdl)}
                    onCorrigerChamps={champs => Object.entries(champs).forEach(([c, v]) => up(c, v))}
                    testid="onb-pdl-recherche" />
                </div>
                {/* LE SENS RETOUR, meme composant que les deux autres portes. Le client
                    qui remplit lui-meme a le compteur chez lui : il recopie le numero,
                    et l'adresse d'Enedis vient confronter la sienne.
                    ⚠️ Pas `authentifie` : ce parcours n'a pas de compte, il passe par la
                    route publique -- plafonnee trois fois plus bas, parce que chaque
                    appel peut poser une declaration de consentement chez un tiers. */}
                <div className="mt-1.5">
                  <RetrouverParPdl client={client}
                    onCorrigerChamps={champs => Object.entries(champs).forEach(([c, v]) => up(c, v))}
                    testid="onb-pdl-retrouver" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] text-neutral-500">{t('field_email', "Email")} *</Label>
                  <Input className="h-9" type="email" value={client.email} onChange={e => up('email', e.target.value)} data-testid="onb-email" />
                  {/* Meme raison qu'au parcours poseur : un bouton grise ne dit pas ce
                      qui manque, et ici il n'y a personne a cote du client pour le lui
                      dire. Phrase en dur pour la meme raison qu'en StepClientInfo : la
                      cle vivrait dans les cinq dictionnaires, hors perimetre de ce lot. */}
                  {!(client.email || '').trim() && (
                    <p className="text-[11px] text-red-500 mt-1" data-testid="onb-email-obligatoire">
                      Obligatoire : votre mandat et le lien d&apos;enregistrement de
                      votre materiel partent a cette adresse.
                    </p>
                  )}
                </div>
                <div><Label className="text-[11px] text-neutral-500">Téléphone</Label><Input className="h-9" value={client.telephone} onChange={e => up('telephone', e.target.value)} data-testid="onb-tel" /></div>
              </div>
            </div>
            <Button className="w-full h-10" disabled={!step1Valid} onClick={allerEnInstallation} data-testid="onb-step1-next">{t('btn_next', "Suivant")} <ArrowRight className="w-4 h-4 ml-1" /></Button>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div className="space-y-4" data-testid="onb-step2">
            <h2 className="text-lg font-semibold text-neutral-900">Votre installation</h2>
            <BandeauMandatExistant mandat={mandatExistant} />
            <div className="rounded-lg border border-neutral-200 bg-white p-4 space-y-3">
              <div>
                <Label className="text-[11px] text-neutral-500">Type d'équipement *</Label>
                <Select value={asset.type} onValueChange={v => upA('type', v)}>
                  <SelectTrigger className="h-9" data-testid="onb-asset-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PAC">Pompe à chaleur</SelectItem>
                    <SelectItem value="PV">Panneaux PV</SelectItem>
                    <SelectItem value="Ballon">Ballon ECS</SelectItem>
                    <SelectItem value="Batterie">Batterie</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[11px] text-neutral-500">{t('lbl_marque', "Marque")} *</Label><Input className="h-9" value={asset.marque} onChange={e => upA('marque', e.target.value)} spellCheck="true" data-testid="onb-marque" /></div>
                <div><Label className="text-[11px] text-neutral-500">{t('lbl_modele', "Modèle")} *</Label><Input className="h-9" value={asset.modele} onChange={e => upA('modele', e.target.value)} spellCheck="true" data-testid="onb-modele" /></div>
              </div>
              <div>
                <Label className="text-[11px] text-neutral-500">Identifiant de l&apos;équipement *</Label>
                <Input className="h-9 font-mono" value={asset.device_id}
                       onChange={e => upA('device_id', e.target.value.trim())}
                       readOnly={!!etiquette?.device_id}
                       data-testid="onb-device-id" />
                {etiquette?.device_id
                  && <p className="text-[10px] text-neutral-400 mt-0.5">Lu sur l&apos;étiquette de la boîte</p>}
                {deviceCheck.loading && <p className="text-[11px] text-neutral-400 mt-1">Vérification...</p>}
                {/* Le message vient du serveur : une seule formulation pour les trois
                    canaux d'enrôlement, et rien à retoucher ici le jour où elle change. */}
                {!deviceCheck.loading && deviceCheck.statut === 'en_ligne' && (
                  <p className="text-[11px] text-emerald-600 mt-1" data-testid="onb-verdict">
                    ✓ {deviceCheck.message}
                  </p>
                )}
                {!deviceCheck.loading && deviceCheck.statut && deviceCheck.statut !== 'en_ligne' && (
                  <p className={`text-[11px] mt-1 ${deviceCheck.statut === 'hors_ligne' ? 'text-amber-600' : 'text-red-600'}`}
                     data-testid="onb-verdict">
                    ✗ {deviceCheck.message}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 h-10" onClick={() => setStep(1)} data-testid="onb-step2-back"><ArrowLeft className="w-4 h-4 mr-1" /> Retour</Button>
              {mandatExistant
                ? <Button className="flex-1 h-10" disabled={!step2Valid || submitting} onClick={submit} data-testid="onb-submit-canal4">
                    {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Validation...</> : <>Valider <Check className="w-4 h-4 ml-1" /></>}
                  </Button>
                : <Button className="flex-1 h-10" disabled={!step2Valid} onClick={() => setStep(3)} data-testid="onb-step2-next">Suivant <ArrowRight className="w-4 h-4 ml-1" /></Button>}
            </div>
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <StepOffre
            offre={offre}
            setOffre={setOffre}
            setStep={setStep}
            valide={!!offre}
            onSuivant={() => { if (offre === OFFRE_ADHESION) setStep(4); else submit(); }}
          />
        )}

        {step === 4 && (
          <div className="space-y-4" data-testid="onb-step3">
            <h2 className="text-lg font-semibold text-neutral-900">Consentements et signature</h2>
            <div className="rounded-lg border border-neutral-200 bg-white p-4 space-y-3">
              <ClausesConsentement
                consents={consents}
                setConsents={setConsents}
                compact
                route="/onboarding/texte-consentements"
              />
              {Object.entries(CONSENT_LABELS).map(([k, lbl]) => (
                <label key={k} className="flex items-start gap-2 cursor-pointer">
                  <Checkbox checked={consents[k]} onCheckedChange={(v) => setConsents(c => ({ ...c, [k]: !!v }))} data-testid={`onb-consent-${k}`} />
                  <span className="text-[12px] text-neutral-700 leading-snug">{lbl}</span>
                </label>
              ))}
            </div>
            <div className="rounded-lg border border-neutral-200 bg-white p-4">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[11px] text-neutral-500">Votre signature</Label>
                <button type="button" className="text-[11px] text-neutral-500 underline" onClick={clearSig} data-testid="onb-sig-clear">Effacer</button>
              </div>
              <canvas ref={canvasRef} width={400} height={150} className="w-full h-32 border border-neutral-200 rounded bg-white touch-none"
                onMouseDown={startDraw} onMouseMove={drawMove} onMouseUp={endDraw} onMouseLeave={endDraw}
                onTouchStart={startDraw} onTouchMove={drawMove} onTouchEnd={endDraw}
                data-testid="onb-signature" />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 h-10" onClick={() => setStep(3)} data-testid="onb-step3-back"><ArrowLeft className="w-4 h-4 mr-1" /> Retour</Button>
              <Button className="flex-1 h-10" disabled={!step3Valid || submitting} onClick={submit} data-testid="onb-submit">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Validation...</> : <>Valider <Check className="w-4 h-4 ml-1" /></>}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
